// Services.jsx
// Page displaying all services offered
// DEMONSTRATES: How to use reusable components with map()

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import ServiceCard from '../components/ServiceCard';
import '../styles/Services.css';

function Services() {
  const { t } = useLanguage();

  return (
    <div className="services-page">
      <section className="services">
        {/* Page Header */}
        <h2 className="section-title">{t.services.title}</h2>
        <p className="section-subtitle">{t.services.subtitle}</p>

        {/* Services Grid */}
        {/*
          KEY CONCEPT - MAPPING OVER DATA:
          
          Instead of manually writing 6 ServiceCard components:
          <ServiceCard ... />
          <ServiceCard ... />
          <ServiceCard ... />
          
          We map over the services array from translations:
          This is the power of React - dynamic rendering!
        */}
        <div className="services-grid">
          {t.services.items.map((service) => (
            /*
              For each service in the array, create a ServiceCard
              
              Props being passed:
              - key: Unique identifier (React needs this for performance)
              - icon: The emoji/icon for the service
              - title: Service name
              - description: Service description
              - features: Array of features
              
              This demonstrates COMPONENT REUSABILITY!
            */
            <ServiceCard
              key={service.id}
              icon={service.icon}
              title={service.title}
              description={service.description}
              features={service.features}
            />
          ))}
        </div>

        {/* Pricing Information */}
        <div className="pricing-info">
          <p>
            <strong>{t.services.pricing}</strong> {t.services.pricingText}
          </p>
        </div>
      </section>
    </div>
  );
}

export default Services;

/*
DEEP DIVE - THE MAP FUNCTION:

What's happening:
1. t.services.items is an array with 6 service objects
2. .map() loops through each object
3. For each object, we create a <ServiceCard> component
4. We pass the object's properties as props
5. React renders all 6 ServiceCards

Why is this better than manual?
- If we add a 7th service to translations, it automatically appears
- If we remove a service, it automatically disappears
- No code changes needed!
- Same component, different data

EXAMPLE:
If t.services.items looks like:
[
  { id: 1, icon: "👗", title: "Blouses", ... },
  { id: 2, icon: "👘", title: "Sarees", ... },
]

The map creates:
<ServiceCard key={1} icon="👗" title="Blouses" ... />
<ServiceCard key={2} icon="👘" title="Sarees" ... />

This is called DATA-DRIVEN RENDERING - the data controls what's displayed!
*/

/*
WHY KEY PROP IS IMPORTANT:

key={service.id} helps React identify which items have:
- Changed
- Been added
- Been removed

Without keys, React has to re-render everything.
With keys, React only updates what changed.

This makes your app MUCH faster!

Rules for keys:
1. Must be unique among siblings
2. Should be stable (don't use random numbers)
3. Use IDs from data when possible
*/
